import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  createProvider,
  deleteProviderById,
  getAllProviders,
  updateProviderById,
  updateProviderPhotoById,
} from '@services/api/provider.api'
import { Provider, ProviderData } from '@dataTypes/provider.types'
import { RootState } from '@store/index'
import { fetchAllContacts } from './contact.action'
import { fetchAllConversations } from './chat.action'
import { fetchAllInternalChats } from './chat-internal.action'
import { fetchAllCallLogs } from './calls.action'

export const fetchAllProviders = createAsyncThunk(
  'providers/fetchAllProviders',
  async (_, { rejectWithValue, getState }) => {
    try {
      const clinicId = (getState() as RootState).clinic.selectedClinic
        ?.clinic_id
      const response = await getAllProviders(clinicId)

      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }

      if (
        response.data &&
        'providers' in response.data &&
        Array.isArray(response.data.providers)
      ) {
        return response.data.providers as Provider[]
      }

      return rejectWithValue('Providers not found in the response')
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'An error occurred',
      )
    }
  },
)

export const addProvider = createAsyncThunk(
  'providers/addProvider',
  async (
    providerData: ProviderData,
    { dispatch, getState, rejectWithValue },
  ) => {
    const state = getState() as RootState
    const selectedClinicId = state.clinic.selectedClinic?.clinic_id

    const user = JSON.parse(localStorage.getItem('user') || '{}')
    const response = await createProvider({
      ...providerData,
      createdBy: user.user_id,
      updatedBy: user.user_id,
    })
    if (response.statusCode !== 201) {
      return rejectWithValue(response.message || 'Failed to add provider')
    }
    if (response.data && 'provider' in response.data) {
      const provider = response.data.provider as Provider
      // Fetch contacts after successful provider creation
      dispatch(fetchAllContacts())
      return { provider, selectedClinicId }
    }
    return rejectWithValue('Provider not found in the response')
  },
)

export const updateProvider = createAsyncThunk<
  Provider,
  { id: string; provider: Partial<ProviderData> },
  { rejectValue: string }
>(
  'providers/updateProvider',
  async ({ id, provider }, { dispatch, rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const response = await updateProviderById(id, {
        ...provider,
        updatedBy: user.user_id,
      })
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message || 'Failed to update provider')
      }

      dispatch(fetchAllContacts())
      dispatch(fetchAllConversations())
      dispatch(fetchAllInternalChats())
      dispatch(fetchAllCallLogs())
      // API may return data as Provider (ApiResponse<Provider>) or as { provider: Provider }
      if (response.data && 'provider' in response.data) {
        return response.data.provider as Provider
      }
      if (
        response.data &&
        typeof response.data === 'object' &&
        'provider_id' in response.data
      ) {
        return response.data as Provider
      }
      return rejectWithValue('Provider not found in the response')
    } catch (error) {
      return rejectWithValue('Failed to update provider')
    }
  },
)

export const deleteProvider = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>(
  'providers/removeProvider',
  async (providerId: string, { rejectWithValue }) => {
    if (!providerId) {
      return rejectWithValue('Provider ID is required for deletion.')
    }
    try {
      const response = await deleteProviderById(providerId)

      if (response.statusCode !== 200) {
        return rejectWithValue(response.message || 'Failed to delete provider')
      }
      return providerId
    } catch (error) {
      return rejectWithValue('Failed to delete provider')
    }
  },
)

export const updateProviderPhoto = createAsyncThunk<
  Provider,
  { providerId: string; file: File },
  { rejectValue: string }
>(
  'providers/updateProviderPhoto',
  async ({ providerId, file }, { dispatch, rejectWithValue }) => {
    try {
      if (!providerId) {
        return rejectWithValue('Provider ID is required.')
      }
      if (!file) {
        return rejectWithValue('File is required.')
      }

      const response = await updateProviderPhotoById(providerId, file)
      if (response.statusCode !== 200) {
        throw new Error(response.message || 'Failed to update provider photo')
      }

      dispatch(fetchAllContacts())
      dispatch(fetchAllConversations())
      dispatch(fetchAllInternalChats())
      dispatch(fetchAllCallLogs())

      if (response.data?.provider) {
        return response.data.provider
      }

      return rejectWithValue('Provider not found in response')
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'An error occurred',
      )
    }
  },
)
